import Card from '@/components/Card';
import { Button } from '@/components/Button';
import { Avatar } from '@/components/Avatar';
import { Input } from '@/components/Controls/Input';
import { Switch } from '@/components/Controls/Switch';
import { FormField } from '@/components/Form/FormField';
import { useEffect, useState, useRef } from 'react';
import { Controller, FieldValues, Path, PathValue } from 'react-hook-form';
import { Select } from '@/components/Controls/Select';
import { Checkbox } from '@/components/Controls/Checkbox';
import {
  useGetMatchingGroupError,
  useListAllDistributors,
} from '@/hooks/group.hooks';
import { TGroupSchema } from '@/schemas/group/group.schema';
import { FormManagementProps } from '@/type/form.types';
import { LUCIUS_GROUP_ID } from '@/api/entities/group';
import Modal from '@/components/Modal';
import { showModal } from '@/utils/modal.utils';
import { setGroupRequiredFields } from '@/utils/required-fields/group.utils';
import { DateTimePicker } from '@/components/Custom/DateTimePicker';
import dayjs from 'dayjs';
import { ErrorMessage } from '@/components/Message/ErrorMessage';
import { TitleSection } from '@/components/Header/Section/TitleSection';
import { useAuth, useUserRole } from '@/hooks/auth.hooks';
import { allFieldErrorsEmpty, resetFieldError } from '@/utils/object.utils';
import { FieldError } from '@/type/error.types';

export const GroupInfosManagement = <TSchema extends FieldValues>({
  t,
  data,
  watch,
  control,
  register,
  setValue,
  setError,
  errors,
  submitDisabled,
  onSubmit,
}: FormManagementProps<TSchema>) => {
  const { loggedInUser } = useAuth();
  const { isAdmin } = useUserRole();
  const emailFieldRef = useRef<HTMLDivElement>(null);
  const [errorMessage, setErrorMessage] = useState('');

  const { data: allDistributors, isLoading: isLoadingAllDistributors } =
    useListAllDistributors({
      userId: isAdmin ? undefined : loggedInUser?.id,
      excludeDefault: true,
    });

  /**
   * === Set Value Watchers section ===
   */
  const nameValue = watch && watch('name' as Path<TSchema>);

  const businessIdNumberValue =
    watch && watch('businessIdNumber' as Path<TSchema>);

  const emailValue = watch && watch('email' as Path<TSchema>);

  const activeValue = watch && watch('active' as Path<TSchema>);
  /**
   * === END OF Set Value Watchers section ===
   */

  const { data: matchingGroupError } = useGetMatchingGroupError({
    id: data?.id,
    name: nameValue,
    businessIdNumber: businessIdNumberValue,
    email: emailValue,
  });

  /**
   * === Reset Error Messages On Value Change section ===
   */
  useEffect(() => {
    if (nameValue) {
      resetFieldError<TSchema>({ fieldName: 'name', setError });
    }

    if (businessIdNumberValue) {
      resetFieldError<TSchema>({
        fieldName: 'businessIdNumber',
        setError,
      });
    }

    if (emailValue) {
      resetFieldError<TSchema>({ fieldName: 'email', setError });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameValue, businessIdNumberValue, emailValue]);
  /**
   * === END OF Reset Error Messages On Value Change section ===
   */

  /**
   * === Set Fields Initial Values section ===
   */
  useEffect(() => {
    if (setValue && data) {
      setGroupRequiredFields({ setValue, data });
      setValue('logo' as Path<TSchema>, data.logo ?? '');
      setValue(
        'distributorId' as Path<TSchema>,
        (data.distributorId === LUCIUS_GROUP_ID ? '' : data.distributorId) ??
          '',
      );
      setValue('isDistributor' as Path<TSchema>, data.isDistributor);
      setValue('isBranch' as Path<TSchema>, data.isBranch);
      setValue('state' as Path<TSchema>, data.state ?? '');
      setValue('addressLine2' as Path<TSchema>, data.addressLine2 ?? '');
      setValue('isWhiteLabel' as Path<TSchema>, data.isWhiteLabel);
      setValue(
        'groupSubscription.startDate' as Path<TSchema>,
        data?.groupSubscription?.startDate
          ? (dayjs(data?.groupSubscription?.startDate) as PathValue<
              TSchema,
              Path<TSchema>
            >)
          : null,
      );
      setValue(
        'groupSubscription.endDate' as Path<TSchema>,
        data?.groupSubscription?.endDate
          ? (dayjs(data?.groupSubscription?.endDate) as PathValue<
              TSchema,
              Path<TSchema>
            >)
          : null,
      );
      setValue('active' as Path<TSchema>, data.active);
    }
  }, [setValue, data]);
  /**
   * === END OF Set Fields Initial Values section ===
   */

  const getDistributorSelectOptions = () => {
    if (
      !isLoadingAllDistributors &&
      allDistributors &&
      allDistributors.data.length > 0
    ) {
      return allDistributors.data.map((distributor: TGroupSchema) => {
        const { id, name } = distributor;

        return {
          value: id,
          label: name,
        };
      });
    }

    return undefined;
  };

  /**
   * === Handle Submit section ===
   */
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const groupErrorMessages = matchingGroupError?.data?.errorMessages;

    if (groupErrorMessages?.includes(FieldError.NAME_EXISTS)) {
      setError('name' as Path<TSchema>, {
        message: t('common:groupNameAlreadyExists'),
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    if (groupErrorMessages?.includes(FieldError.BUSINESS_ID_NUMBER_EXISTS)) {
      setError('businessIdNumber' as Path<TSchema>, {
        message: t('common:groupBusinessIdNumberAlreadyExists'),
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    if (groupErrorMessages?.includes(FieldError.EMAIL_EXISTS)) {
      setError('email' as Path<TSchema>, {
        message: t('common:groupEmailAlreadyExists'),
      });
      emailFieldRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }

    if (allFieldErrorsEmpty(errors)) {
      onSubmit?.();
    }
  };
  /**
   * === END OF Handle Submit section ===
   */

  return (
    <>
      <form onSubmit={handleSubmit}>
        <Card
          title={
            <TitleSection
              text={t('common:informations')}
              defaultIconClassName="ki-badge"
            />
          }
          footer={
            <Button
              buttonClassName="px-8"
              text={
                submitDisabled ? `${t('common:loading')}...` : t('common:save')
              }
              disabled={submitDisabled}
            />
          }
          bodyClassName="py-8 flex flex-col gap-5"
        >
          <FormField
            text={t('common:logo')}
            containerClassName="mb-2 max-md:mb-0"
            textWrapperClassName="mt-3 max-md:mt-0"
          >
            <Avatar
              outerContainerClassName="max-md:w-full"
              containerClassName="max-md:mx-auto"
              imageUrl={data?.logo}
              allowedFormats={['svg']}
              onShowErrorMessage={(message) => {
                setErrorMessage(message);
                showModal('#group-image-error-modal');
              }}
              onChange={(url) =>
                setValue?.(
                  'logo' as Path<TSchema>,
                  url as PathValue<TSchema, Path<TSchema>>,
                )
              }
            />
          </FormField>
          <FormField
            text={t('common:groupName')}
            required
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register && register('name' as Path<TSchema>))}
                errorMessage={errors && (errors.name?.message as string)}
                placeholder={t('common:groupName')}
                type="search"
              />
            </div>
          </FormField>
          <FormField text={t('common:distributor')} textWrapperClassName="mt-3">
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Controller
                name="distributorId"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    errorMessage={
                      errors && (errors.distributorId?.message as string)
                    }
                    placeholder={t('common:chooseTheDistributor')}
                    options={getDistributorSelectOptions()}
                  />
                )}
              />
            </div>
          </FormField>
          <FormField text={t('common:type')}>
            <div className="flex flex-1 flex-col items-start gap-3">
              <Checkbox
                {...(register && register('isDistributor' as Path<TSchema>))}
                errorMessage={
                  errors && (errors.isDistributor?.message as string)
                }
                text={t('common:distributor')}
              />
              <Checkbox
                {...(register && register('isBranch' as Path<TSchema>))}
                errorMessage={errors && (errors.isBranch?.message as string)}
                text={t('common:branch')}
              />
            </div>
          </FormField>
          <FormField
            text={t('common:companyIdNumber')}
            required
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register && register('businessIdNumber' as Path<TSchema>))}
                errorMessage={
                  errors && (errors.businessIdNumber?.message as string)
                }
                placeholder={t('common:companyIdNumber')}
                type="search"
              />
            </div>
          </FormField>
          <FormField
            text={t('common:addressLine1')}
            required
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register && register('addressLine1' as Path<TSchema>))}
                errorMessage={
                  errors && (errors.addressLine1?.message as string)
                }
                placeholder={t('common:addressLine1')}
                type="search"
              />
            </div>
          </FormField>
          <FormField
            text={t('common:addressLine2')}
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register && register('addressLine2' as Path<TSchema>))}
                errorMessage={
                  errors && (errors.addressLine2?.message as string)
                }
                placeholder={t('common:addressLine2')}
                type="search"
              />
            </div>
          </FormField>
          <FormField
            text={t('common:postalCode')}
            required
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register && register('postalCode' as Path<TSchema>))}
                errorMessage={errors && (errors.postalCode?.message as string)}
                placeholder={t('common:postalCode')}
                type="search"
              />
            </div>
          </FormField>
          <FormField
            text={t('common:city')}
            required
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register && register('city' as Path<TSchema>))}
                errorMessage={errors && (errors.city?.message as string)}
                placeholder={t('common:city')}
                type="search"
              />
            </div>
          </FormField>
          <FormField text={t('common:state')} textWrapperClassName="mt-3">
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register && register('state' as Path<TSchema>))}
                errorMessage={errors && (errors.state?.message as string)}
                placeholder={t('common:state')}
                type="search"
              />
            </div>
          </FormField>
          <FormField
            text={t('common:country')}
            required
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register && register('country' as Path<TSchema>))}
                errorMessage={errors && (errors.country?.message as string)}
                placeholder={t('common:country')}
                type="search"
              />
            </div>
          </FormField>
          <FormField
            text={t('common:phoneNumber')}
            required
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register && register('phone' as Path<TSchema>))}
                errorMessage={errors && (errors.phone?.message as string)}
                placeholder={t('common:phoneNumber')}
                type="search"
              />
            </div>
          </FormField>
          <FormField
            containerRef={emailFieldRef}
            text={t('common:email')}
            required
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register && register('email' as Path<TSchema>))}
                errorMessage={errors && (errors.email?.message as string)}
                placeholder={t('common:email')}
                type="search"
              />
            </div>
          </FormField>
          <FormField
            text={t('common:whiteLabel')}
            required
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Checkbox
                {...(register && register('isWhiteLabel' as Path<TSchema>))}
                errorMessage={
                  errors && (errors.isWhiteLabel?.message as string)
                }
                text={t('common:whiteLabel')}
                textClassName="text-gray-500"
              />
            </div>
          </FormField>
          {isAdmin && (
            <>
              {data?.groupSubscription?.startDate && (
                <FormField
                  text={t('common:subscriptionStartDate')}
                  textWrapperClassName="mt-3"
                >
                  <div className="flex flex-1 flex-wrap items-start gap-5">
                    <div className="flex flex-1 flex-col gap-0.75">
                      <Controller
                        name="groupSubscription.startDate"
                        control={control}
                        render={({ field }) => (
                          <DateTimePicker<'groupSubscription.startDate'>
                            field={field}
                            variant="mobile"
                            useTime={false}
                            disabled={Boolean(
                              data?.groupSubscription?.startDate,
                            )}
                            value={field.value ? dayjs(field.value) : null}
                            onChange={(date) => {
                              field.onChange(date?.toDate() || null);
                            }}
                          />
                        )}
                      />
                      <ErrorMessage
                        message={
                          errors?.['groupSubscription.startDate']
                            ?.message as string
                        }
                      />
                    </div>
                  </div>
                </FormField>
              )}
              <FormField
                text={t('common:subscriptionEndDate')}
                textWrapperClassName="mt-3"
              >
                <div className="flex flex-1 flex-wrap items-start gap-5">
                  <div className="flex flex-1 flex-col gap-0.75">
                    <Controller
                      name="groupSubscription.endDate"
                      control={control}
                      render={({ field }) => (
                        <DateTimePicker<'groupSubscription.endDate'>
                          field={field}
                          variant="mobile"
                          useTime={false}
                          value={field.value ? dayjs(field.value) : null}
                          onChange={(date) => {
                            field.onChange(date?.toDate() || null);
                          }}
                        />
                      )}
                    />
                    <ErrorMessage
                      message={
                        errors?.['groupSubscription.endDate']?.message as string
                      }
                    />
                  </div>
                </div>
              </FormField>
            </>
          )}
          <FormField
            text={t('common:activeStatus')}
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5 mt-3">
              <Switch
                {...(register && register('active' as Path<TSchema>))}
                errorMessage={errors && (errors.active?.message as string)}
                checked={data ? activeValue : true}
              />
            </div>
          </FormField>
        </Card>
      </form>
      <Modal
        id="group-image-error-modal"
        title={t('common:imageIsIncorrect')}
        centerY
        size="small"
        showCancelBtn={false}
        showSecondBtn
        secondBtnLabel={t('common:ok')}
      >
        <p>{errorMessage}</p>
      </Modal>
    </>
  );
};
